import { useMemo } from 'react'

import { Separator } from '@/components/ui/separator'
import { TopFansStats } from '@/modules/statistics/onlyfans/ui/TopFansStats'

import type { useLinkTrackingById } from '../../query/link-tracking-id-query'
import {
  EMPTY_ENGAGEMENT,
  EMPTY_SPENDING_PIE_CHART,
  EMPTY_SUBSCRIBED_PIE_CHART,
  type LinkType,
} from '../../types'
import { ClaimedFansChart } from './charts/claimed-fans-chart'
import { FanEngagementChart } from './charts/fan-engagement-chart'
import { SpendingFansChart } from './charts/spending-fans-chart'
import { ProfitView } from './profit-view'
import { PromotionCostsController } from './promotion-costs-controller'
import { ReturnOnInvestView } from './return-on-invest-view.tsx'
import { TrackingStatisticsCard } from './tracking-statistics-card'

interface TrackingLinksInspectViewProps
  extends ReturnType<typeof useLinkTrackingById> {
  type: LinkType
  isFetching: boolean
  showCents?: boolean
}

function calculateReturnOnInvestment(
  profit: number,
  promotionCost: number,
): number {
  if (promotionCost === 0) {
    return 0
  }

  return profit / promotionCost
}

export const TrackingLinksInspectView = ({
  data,
  type,
  isFetching,
  updatePromotionCost,
  showCents,
}: TrackingLinksInspectViewProps) => {
  const isTrackingType = type === 'Tracking'

  const returnOnInvestment = useMemo(() => {
    return calculateReturnOnInvestment(
      data?.investment?.profit || 0,
      data?.link?.promotionCost || 0,
    )
  }, [data])

  const averageFanSpend = useMemo(() => {
    const totalRevenue = data?.totalRevenue || 0
    const claimedFans = data?.claimedFans || 0

    if (claimedFans === 0 || totalRevenue == 0) {
      return 0
    }

    return totalRevenue / claimedFans
  }, [data])

  return (
    <div className="grid gap-[32px]">
      <PromotionCostsController
        promotionCost={data?.link?.promotionCost}
        isFetching={isFetching}
        updatePromotionCost={updatePromotionCost}
      />
      <Separator />
      <ProfitView
        showCents={showCents}
        profit={data?.investment?.profit}
      />
      <Separator />
      <ReturnOnInvestView returnOnInvest={returnOnInvestment} />
      <Separator />
      <TrackingStatisticsCard
        linkClicks={data?.link?.trackingAttributes?.transitions || 0}
        claimedFans={data?.claimedFans || 0}
        claimedFansInLast30Days={data?.claimedFansInLast30Days || 0}
        revenue={data?.totalRevenue || 0}
        revenueInLast30Days={data?.revenueInLast30Days || 0}
        averageFanSpend={averageFanSpend}
        renewActive={data?.renewActive || 0}
        totalFans={data?.claimedFans || 0}
        isTrackingType={isTrackingType}
        showCents={showCents}
      />

      <div className="mb-4 grid gap-4">
        <div className="flex flex-col gap-8 md:flex-row">
          {isTrackingType && (
            <ClaimedFansChart
              subscribedPieChart={
                data?.subscribedPieChart || EMPTY_SUBSCRIBED_PIE_CHART
              }
            />
          )}
          <SpendingFansChart
            spendingPieChart={
              data?.spendingPieChart || EMPTY_SPENDING_PIE_CHART
            }
          />
          <div className="h-full w-full">
            <TopFansStats
              title="Claimed Fans"
              topFanList={data?.bestFans?.fans || []}
              isFetching={false}
              fullHeight
              enablePagination
              itemsPerPage={5}
            />
          </div>
        </div>
      </div>
      <div className="h-[30rem]">
        <FanEngagementChart engagement={data?.engagement || EMPTY_ENGAGEMENT} />
      </div>
    </div>
  )
}
